import React, { useState, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { jsPDF } from "jspdf";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFilePdf,
  faCloudUploadAlt,
  faTimes,
  faRedo
} from "@fortawesome/free-solid-svg-icons";

const JpgToPdfConverter = () => {
  const { darkMode } = useTheme();
  const [images, setImages] = useState([]);
  const [pdfName, setPdfName] = useState("converted");
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState("");
  const [layout, setLayout] = useState("portrait");
  const [margin, setMargin] = useState(10);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);

  const handleFileUpload = (files) => {
    setError("");
    if (!files || files.length === 0) return;

    // Filter only image files
    const imageFiles = files.filter(
      (file) =>
        file.type.match("image.*") &&
        (file.name.endsWith(".jpg") || file.name.endsWith(".jpeg"))
    );

    if (imageFiles.length === 0) {
      setError("Please upload valid JPG/JPEG images.");
      return;
    }

    // Create image objects with preview URLs
    const newImages = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
    }));

    setImages((prev) => [...prev, ...newImages]);

    // Set PDF name based on first file if no name is set yet
    if (!pdfName || pdfName === "converted") {
      setPdfName(newImages[0].name);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  const convertToPdf = async () => {
    if (images.length === 0) {
      setError("Please upload at least one image.");
      return;
    }

    setIsConverting(true);
    setError("");

    try {
      // Create a new PDF document
      const pdf = new jsPDF({
        orientation: layout,
        unit: "mm",
      });

      // Process each image
      for (let i = 0; i < images.length; i++) {
        const img = images[i];

        // Create an image element to get dimensions
        const imgElement = await loadImage(img.preview);

        // Calculate dimensions to fit page with margins
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const availableWidth = pageWidth - margin * 2;
        const availableHeight = pageHeight - margin * 2;

        let imgWidth = imgElement.width;
        let imgHeight = imgElement.height;

        // Scale image to fit available space while maintaining aspect ratio
        const widthRatio = availableWidth / imgWidth;
        const heightRatio = availableHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);

        imgWidth = imgWidth * ratio;
        imgHeight = imgHeight * ratio;

        // Center the image on the page
        const x = (pageWidth - imgWidth) / 2;
        const y = (pageHeight - imgHeight) / 2;

        // Add image to PDF
        pdf.addImage(img.preview, "JPEG", x, y, imgWidth, imgHeight);

        // Add a new page if this isn't the last image
        if (i < images.length - 1) {
          pdf.addPage();
        }
      }

      // Save the PDF
      pdf.save(`${pdfName}.pdf`);
    } catch (err) {
      setError("Error converting images to PDF. Please try again.");
      console.error(err);
    } finally {
      setIsConverting(false);
    }
  };

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview); // Clean up memory
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const clearAll = () => {
    images.forEach((img) => URL.revokeObjectURL(img.preview)); // Clean up memory
    setImages([]);
    setError("");
    setPdfName("converted");
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      <HeaderSection toolId="jpgToPdf" />
      <div className={`max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700 shadow-gray-900/10' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Header Section */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faFilePdf} className="mr-3" />
              JPG to PDF Converter
            </h1>
            <p className="opacity-90">
              Convert JPG/JPEG images to PDF files with high quality
            </p>
          </div>

          {/* Upload Area */}
          <section 
            ref={uploadAreaRef}
            className={`border-2 border-dashed rounded-xl py-10 px-4 md:px-20 m-6 text-center cursor-pointer transition-all duration-300 ${
              isDragging 
                ? 'border-green-500 bg-green-100 dark:bg-green-900/30' 
                : images.length > 0
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-300 hover:border-green-400 hover:bg-green-50 dark:hover:bg-gray-700'
            }`}
            onClick={triggerFileInput}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FontAwesomeIcon 
              icon={faCloudUploadAlt} 
              className={`text-4xl md:text-5xl mb-4 p-6 transition-transform duration-300 hover:scale-110 ${
                darkMode ? 'text-green-400' : 'text-green-500'
              }`} 
            />
            <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
              Drag & drop your JPG images here or click to browse
            </p>
            <input 
              type="file" 
              ref={fileInputRef}
              className="hidden" 
              accept=".jpg,.jpeg"
              onChange={handleInputChange}
              multiple
            />
            {images.length > 0 && (
              <div className="mt-4 text-sm font-medium flex items-center justify-center gap-2">
                <span className={darkMode ? 'text-gray-200' : 'text-green-600'}>
                  {images.length} image{images.length !== 1 ? 's' : ''} selected
                </span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className={`transition-colors duration-300 ${
                    darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'
                  }`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </section>

          {/* Settings Section */}
          {images.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-6">
              <div>
                <label htmlFor="pdf-name" className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  PDF File Name
                </label>
                <input
                  type="text"
                  id="pdf-name"
                  value={pdfName}
                  onChange={(e) => setPdfName(e.target.value)}
                  className={`w-full border rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                />
              </div>
              <div>
                <label htmlFor="layout" className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Page Layout
                </label>
                <select
                  id="layout"
                  value={layout}
                  onChange={(e) => setLayout(e.target.value)}
                  className={`w-full border rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
              <div>
                <label htmlFor="margin" className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Margin (mm)
                </label>
                <input
                  type="number"
                  id="margin"
                  min="0"
                  max="50"
                  value={margin}
                  onChange={(e) => setMargin(parseInt(e.target.value))}
                  className={`w-full border rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={convertToPdf}
                  disabled={isConverting || images.length === 0}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                    images.length === 0 || isConverting
                      ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-70 text-gray-600 dark:text-gray-300'
                      : darkMode
                        ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  {isConverting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Converting...
                    </span>
                  ) : (
                    'Convert to PDF'
                  )}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mx-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${
                    darkMode ? 'text-red-300' : 'text-red-700'
                  }`}>{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results Section */}
          {images.length > 0 && (
            <div className="mt-6 mx-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-xl font-semibold ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>Selected Images ({images.length})</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {images.map((image, index) => (
                  <div key={index} className={`border rounded-lg overflow-hidden shadow-sm transition-colors duration-300 ${
                    darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className={`p-2 border-b flex justify-between items-center ${
                      darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-100'
                    }`}>
                      <span className={`text-sm font-medium ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>{image.file.name}</span>
                      <button
                        onClick={() => removeImage(index)}
                        className={`flex items-center gap-1 text-xs py-1 px-2 rounded transition-all duration-300 hover:-translate-y-0.5 ${
                          darkMode
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : 'bg-red-500 hover:bg-red-600 text-white'
                        }`}
                      >
                        <FontAwesomeIcon icon={faTimes} size="xs" />
                        Remove
                      </button>
                    </div>
                    <div className={`p-2 flex justify-center ${
                      darkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <img 
                        src={image.preview} 
                        alt={`Preview ${index + 1}`}
                        className="max-w-full h-auto border rounded"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {images.length > 0 && (
            <footer className="flex flex-col sm:flex-row gap-3 m-6">
              <button
                onClick={clearAll}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                  darkMode
                    ? 'border border-green-400 text-green-400 hover:bg-gray-700'
                    : 'border border-green-500 text-green-600 hover:bg-green-50'
                }`}
              >
                <FontAwesomeIcon icon={faRedo} />
                Reset
              </button>
            </footer>
          )}
        </div>
      </div>
      <BottomSection toolId="jpgToPdf" />
    </>
  );
};

export default JpgToPdfConverter;